import { useSettlementToken } from "features/tokens/useSettlementToken";
import React, { useState } from "react";
import { useSelector } from "react-redux";
import "./card-row.css";
import StatusBadge, {
  StatusVariant,
} from "components/status-badge/StatusBadge";
import Button from "components/button/Button";
import OverflowMenu, {
  OverflowMenuAction,
} from "components/overflow-menu/OverflowMenu";
import LoadingModal from "modals/loading/LoadingModal";
import ErrorModal from "modals/error-modal/ErrorModal";
import SuccessModal from "modals/success-modal/SuccessModal";
import { useBlockCard } from "features/cards/useBlockCard";
import { useCardStatusToggle } from "features/cards/useCardStatusToggle";
import TopUpModal from "features/cards/topup/TopUpModal";
import ApprovalsModal from "features/approvals/ApprovalsModal";
import ReplaceCardForm from "features/cards/ReplaceCardForm";
import type { Network } from "@gardio/contracts/polygon";
import { networkTypeSelector } from "store/userSliceHelpers";
import CardNumber from "components/card-number/CardNumber";
import {
  AngleDown,
  CopyIconV1,
  CheckCircleIcon,
  CoinsIconV1,
  ReplaceIconV1,
  BanIconV1,
  PauseCircleIconV1,
  PlayCircleIconV1,
} from "images/image-component";

export interface CardRecord {
  cardNumber: string;
  aliasAddress?: string;
  balance?: number;
  /** Overrides the selected token's label. Normally omitted — the row reads the selection, so
   *  the unit can never disagree with the token the balance was fetched in. */
  balanceUnit?: string;
  status: StatusVariant;
}

interface Props extends CardRecord {
  /** Override the session network; defaults to the one chosen at login. */
  network?: Network;
}

function CardRow({
  cardNumber,
  aliasAddress,
  balance,
  balanceUnit,
  status,
  network,
}: Props) {
  const { label: selectedTokenLabel } = useSettlementToken();
  // TopUpModal signs a real transfer, and `network` picks its RPC URL and token
  // contract (see useTopUpSigning) — so this has to follow the session rather
  // than default to a literal, or every top-up would go to Amoy regardless of
  // what was selected at login.
  const sessionNetwork = useSelector(networkTypeSelector);
  const activeNetwork = network ?? sessionNetwork;
  const [topUpOpen, setTopUpOpen] = useState(false);
  const [approvalsOpen, setApprovalsOpen] = useState(false);
  const [replaceOpen, setReplaceOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const {
    blockingCard,
    blockingCardMsg,
    blockCardError,
    blockCardErrorMsg,
    blockCardSuccess,
    blockCardSuccessMsg,
    handleBlockCard,
    handleRetry,
    handleCloseError,
    handleCloseSuccess,
  } = useBlockCard();
  const {
    changingCardStatus,
    changingCardStatusMsg,
    cardStatusError,
    cardStatusErrorMsg,
    cardStatusSuccess,
    cardStatusSuccessMsg,
    handleCardStatusChange,
    handleRetry: handleCardStatusRetry,
    handleCloseError: handleCloseCardStatusError,
    handleCloseSuccess: handleCloseCardStatusSuccess,
  } = useCardStatusToggle();

  const copy = (key: string, value: string) => {
    navigator.clipboard?.writeText(value).then(() => {
      setCopiedKey(key);
      window.setTimeout(
        () => setCopiedKey((k) => (k === key ? null : k)),
        1200,
      );
    });
  };

  const renderCopy = (key: string, value: string) => (
    <button
      type="button"
      className="cr-copy-btn"
      onClick={() => copy(key, value)}
      aria-label={`Copy ${key}`}
    >
      {copiedKey === key ? (
        <CheckCircleIcon size={14} />
      ) : (
        <CopyIconV1 size={14} />
      )}
    </button>
  );

  const ctaIsDisabled =
    status === "Revoked" ||
    status === "Manufactured" ||
    status === "Suspended" ||
    status === "Unassigned";

  // /replaceCard requires the old card to be Active on-chain — that's the only status that
  // still owns the alias wallet a replacement carries over (see treasury_presenters.replaceCard).
  const canReplaceCard = !!aliasAddress && status?.toLowerCase() === "active";

  // Case-folded because the status arrives capitalised from /getCards but lowercase from some
  // callers — see StatusVariant.
  const normalizedStatus = status?.toLowerCase();
  const isSuspended = normalizedStatus === "suspended";
  const canToggleCardStatus = isSuspended || normalizedStatus === "active";

  const overflowActions: OverflowMenuAction[] = [
    {
      label: "Add currency",
      icon: <CoinsIconV1 size={16} />,
      onClick: () => setApprovalsOpen(true),
      disabled: ctaIsDisabled,
    },
    {
      label: "Replace card",
      icon: <ReplaceIconV1 size={16} />,
      onClick: () => setReplaceOpen(true),
      disabled: !canReplaceCard,
    },
    {
      label: isSuspended ? "Activate card" : "Suspend card",
      icon: isSuspended ? (
        <PlayCircleIconV1 size={16} />
      ) : (
        <PauseCircleIconV1 size={16} />
      ),
      onClick: () =>
        handleCardStatusChange(
          isSuspended ? "activate" : "suspend",
          cardNumber,
        ),
      disabled: !canToggleCardStatus,
    },
    {
      label: "Block card",
      icon: <BanIconV1 size={16} color="var(--neg)" />,
      onClick: () => handleBlockCard(cardNumber),
      disabled: status === "Revoked" || status === "Manufactured",
    },
  ];
  return (
    <>
      <div className="cr-row">
        <span className="cr-card-num">
          <CardNumber value={cardNumber} />
        </span>

        <span className="cr-balance">
          {balance !== undefined ? (
            <>
              {balance.toFixed(2)}
              <small>{balanceUnit ?? selectedTokenLabel}</small>
            </>
          ) : (
            <span className="cr-balance-skeleton" />
          )}
        </span>

        <span className="cr-status">
          <StatusBadge status={status} />
        </span>

        <div className="cr-actions">
          <Button
            variant="success"
            size="sm"
            onClick={() => setTopUpOpen(true)}
            disabled={!aliasAddress || ctaIsDisabled}
          >
            Top up
          </Button>
          <div className="cr-actions-end">
            <OverflowMenu
              actions={overflowActions}
              ariaLabel="More card actions"
            />
            <button
              type="button"
              className={`cr-chevron${expanded ? " cr-chevron--open" : ""}`}
              onClick={() => setExpanded((e) => !e)}
              aria-label={expanded ? "Hide details" : "Show details"}
              aria-expanded={expanded}
            >
              <AngleDown size={16} />
            </button>
          </div>
        </div>

        {expanded && (
          <div className="cr-detail">
            <div className="cr-detail-item">
              <span className="cr-detail-k">Address</span>
              <span className="cr-detail-v">
                {aliasAddress ? (
                  <>
                    <span className="cr-mono" title={aliasAddress}>
                      {aliasAddress}
                    </span>
                    {renderCopy("address", aliasAddress)}
                  </>
                ) : (
                  "—"
                )}
              </span>
            </div>
          </div>
        )}
      </div>

      {aliasAddress && (
        <TopUpModal
          isOpen={topUpOpen}
          onClose={() => setTopUpOpen(false)}
          cardNumber={cardNumber}
          cardAliasAddress={aliasAddress}
          network={activeNetwork}
        />
      )}

      {/* Mounted only while open: the audit runs on mount, so unmounting is the reset. */}
      {approvalsOpen && (
        <ApprovalsModal
          isOpen
          kind="card"
          id={cardNumber}
          onClose={() => setApprovalsOpen(false)}
        />
      )}

      {/* Mounted only while open, so its form state resets on every open — see useReplaceCard. */}
      {replaceOpen && (
        <ReplaceCardForm
          isOpen
          cardNumber={cardNumber}
          onClose={() => setReplaceOpen(false)}
        />
      )}

      {blockingCard && (
        <LoadingModal isOpen={blockingCard} loadingMsg={blockingCardMsg} />
      )}
      {blockCardError && (
        <ErrorModal
          isOpen={blockCardError}
          errorMsg={blockCardErrorMsg}
          handleClose={handleCloseError}
          tryAgain={handleRetry}
        />
      )}
      {blockCardSuccess && (
        <SuccessModal
          isOpen={blockCardSuccess}
          message={blockCardSuccessMsg}
          handleClose={handleCloseSuccess}
        />
      )}

      {changingCardStatus && (
        <LoadingModal
          isOpen={changingCardStatus}
          loadingMsg={changingCardStatusMsg}
        />
      )}
      {cardStatusError && (
        <ErrorModal
          isOpen={cardStatusError}
          errorMsg={cardStatusErrorMsg}
          handleClose={handleCloseCardStatusError}
          tryAgain={handleCardStatusRetry}
        />
      )}
      {cardStatusSuccess && (
        <SuccessModal
          isOpen={cardStatusSuccess}
          message={cardStatusSuccessMsg}
          handleClose={handleCloseCardStatusSuccess}
        />
      )}
    </>
  );
}

export default CardRow;
